import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import {
  CLOCK_INS_BY_EMPLOYEE_ID,
  EMPLOYEES_BY_AGENCY_ID,
} from "../../api/query";
import { useEffect, useMemo, useState } from "react";
import Loader from "../../components/common/Loader";
import DynamicTable from "../../components/common/DynamicTable";
import {
  differenceInMinutes,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
} from "date-fns";
import { useNavigate } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import { useAuth } from "../../context/AuthContext";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import MarkPresentPopup from "../../components/attendance/MarkPresentPopup";
import NotifyPopup from "../../components/dashboard/NotifyPopup";
import {
  DELETE_CLOCK_IN_OUT,
  SET_EXTRA_DAY,
  SET_NIGHT_SHIFT,
} from "../../api/mutation";
import {
  showError,
  showSuccess,
} from "../../components/common/ToastNotification";
import { SelectedDate } from "../leave/ApplyLeave";
import LeaveDetailsPopup from "../../components/leave/LeaveDetailsPopup";

function formatDuration(start: string, end: string) {
  const startDate = parseISO(start);
  const endDate = parseISO(end);
  const diffMinutes = differenceInMinutes(endDate, startDate);

  const days = Math.floor(diffMinutes / (60 * 24));
  const hours = Math.floor((diffMinutes % (60 * 24)) / 60);
  const minutes = diffMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

  return parts.join(" ");
}

const formatPrettyDateWithWeekday = (isoDate: string, weekday: string) => {
  try {
    const date = parseISO(isoDate);
    return `${format(date, "MMM dd, yyyy")} (${weekday})`;
  } catch (e) {
    return "-";
  }
};

const formatDateTime = (isoString: string) => {
  try {
    const date = parseISO(isoString);
    return format(date, "MMM dd, yyyy - hh:mm a");
  } catch (e) {
    return "-";
  }
};

const viewAttendanceSchema = yup.object().shape({
  monthName: yup.string().required("Month is required"),
  yearName: yup.string().required("Year is required"),
  assignedMemberID: yup.string().required("Employee is required"),
  assignedMember: yup.string().required("Employee is required"),
});

const ViewAttendance = () => {
  const navigate = useNavigate();
  const { userInfo, isAdmin, developmentUser } = useAuth();

  const employeeID = userInfo?.employee_id;
  const organizationID = userInfo?.agencies?.id || "";

  //constants
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [mainTable, setMainTable] = useState<any>(null);
  const [mainTableUpdate, setMainTableUpdate] = useState(false);

  const [monthOptions, setMonthOptions] = useState<
    { key: string; name: string }[]
  >([]);
  const [yearOptions, setYearOptions] = useState<
    { key: string; name: string }[]
  >([]);
  const [assignedMemberOptions, setAssignedMemberOptions] = useState<
    { key: string; name: string }[]
  >([]);
  const [markPresentPopup, setMarkPresentPopup] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedClockInID, setSelectedClockInID] = useState("");
  const [isLeavePopupOpen, setIsLeavePopupOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<SelectedDate[]>([]);

  const [isExtraDayPopupOpen, setIsExtraDayPopupOpen] = useState(false);
  const [selectedExtraDay, setSelectedExtraDay] = useState("");
  const [isNightShiftPopupOpen, setIsNightShiftPopupOpen] = useState(false);
  const [selectedNightShift, setSelectedNightShift] = useState("");

  //api
  const [
    getClockInsByEmpID,
    { data: clockInsData, loading: clockInsDataLoading },
  ] = useLazyQuery(CLOCK_INS_BY_EMPLOYEE_ID, {
    fetchPolicy: "no-cache",
  });

  const [deleteClockInOut, { loading: deleteClockInOutLoading }] = useMutation(
    DELETE_CLOCK_IN_OUT,
    {
      fetchPolicy: "no-cache",
    },
  );

  const [
    getEmployeesByAgencyID,
    { data: employeesData, loading: employeesDataLoading },
  ] = useLazyQuery(EMPLOYEES_BY_AGENCY_ID, {
    fetchPolicy: "no-cache",
  });

  const [setExtraDay, { loading: setExtraDayLoading }] = useMutation(
    SET_EXTRA_DAY,
    {
      fetchPolicy: "no-cache",
    },
  );

  const [setNightShift, { loading: setNightShiftLoading }] = useMutation(
    SET_NIGHT_SHIFT,
    {
      fetchPolicy: "no-cache",
    },
  );

  const {
    setValue,
    formState: { errors },
    watch,
  } = useForm<any>({
    resolver: yupResolver(viewAttendanceSchema),
  });

  //watch
  const watchMonth = watch("monthName");
  const watchYear = watch("yearName");
  const watchAssignedMember = watch("assignedMember");
  const watchAssignedMemberID = watch("assignedMemberID");

  useEffect(() => {
    const currentDate = new Date();

    const monthNames = Array.from({ length: 12 }, (_, i) => ({
      key: (i + 1).toString().padStart(2, "0"),
      name: format(new Date(2000, i), "MMMM"),
    }));

    const currentYear = currentDate.getFullYear();
    const yearRange = Array.from({ length: 5 }, (_, i) => currentYear - i);
    const yearOptionsData = yearRange.map((year) => ({
      key: year.toString(),
      name: year.toString(),
    }));

    setMonthOptions(monthNames);
    setYearOptions(yearOptionsData);

    // Get current month object
    const defaultMonth = monthNames[currentDate.getMonth()];
    const defaultYear = currentYear.toString();

    setValue("monthName", defaultMonth.key); // form stores key
    setValue("yearName", defaultYear);
  }, []);

  useEffect(() => {
    if (organizationID) {
      getEmployeesByAgencyID({
        variables: {
          agency_id: parseInt(organizationID),
        },
      });
    }
  }, [organizationID]);

  useEffect(() => {
    const selectedMonth = parseInt(watchMonth);
    const selectedYear = parseInt(watchYear);

    if (watchAssignedMemberID && selectedMonth && selectedYear) {
      const firstDayOfMonth = startOfMonth(
        new Date(selectedYear, selectedMonth - 1),
      );
      const lastDayOfMonth = endOfMonth(
        new Date(selectedYear, selectedMonth - 1),
      );
      const today = new Date();

      const isCurrentMonth =
        selectedYear === today.getFullYear() &&
        selectedMonth === today.getMonth() + 1;

      const formattedStartDate = format(firstDayOfMonth, "yyyy-MM-dd");
      const formattedEndDate = format(
        isCurrentMonth ? today : lastDayOfMonth,
        "yyyy-MM-dd",
      );

      getClockInsByEmpID({
        variables: {
          employee_id: parseInt(watchAssignedMemberID),
          start_date: formattedStartDate,
          end_date: formattedEndDate,
        },
      });
    }
  }, [watchMonth, watchYear, watchAssignedMemberID]);

  useEffect(() => {
    if (employeesData && employeesData.employees_by_agency_id) {
      const employeeOptions =
        employeesData?.employees_by_agency_id?.employees?.map((emp: any) => ({
          key: emp.employee_id,
          name: `${emp.first_name} ${emp.last_name}`,
        }));
      setAssignedMemberOptions(employeeOptions);

      const defaultMember = employeeOptions?.find(
        (member: any) => member.key == employeeID,
      );

      if (defaultMember) {
        // Set default assigned member
        setValue("assignedMemberID", defaultMember.key);
        setValue("assignedMember", defaultMember.name);
        // trigger('assignedMember');
      } else {
        // Clear if employeeID not found in members
        setValue("assignedMemberID", "");
        setValue("assignedMember", "");
      }
    }
  }, [employeesData]);

  // Use useMemo to optimize the column generation
  const columns: any[] = [
    {
      key: "date",
      name: "Date",
      id: "date",
      header: "Date",
      accessorKey: "date",
      enableSorting: false,
      cell: ({ row }: any) => (
        <div className="flex gap-4 items-center">
          <span>{row.original.date}</span>

          {row.original.isExtraDay && (
            <span className="text-xs font-semibold text-green-600">
              Extra Day
            </span>
          )}
          {row.original.isNightShift && (
            <span className="text-xs font-semibold text-blue-600">
              Night Shift
            </span>
          )}
        </div>
      ),
    },
    {
      key: "checkInTime",
      name: "Clock In Time",
      id: "checkInTime",
      header: "Clock In Time",
      accessorKey: "checkInTime",
      enableSorting: false,
    },
    {
      key: "checkOutTime",
      name: "Clock Out Time",
      id: "checkOutTime",
      header: "Clock Out Time",
      accessorKey: "checkOutTime",
      enableSorting: false,
    },
    {
      key: "duration",
      name: "Duration",
      id: "duration",
      header: "Duration",
      accessorKey: "duration",
      enableSorting: false,
    },
    {
      key: "action",
      name: "Action",
      id: "action",
      header: "Action",
      accessorKey: "action",
      hideSearch: true,
      cell: ({ row }: any) => {
        const allowedAbsentIDs = isAdmin;
        const allowedPresentIDs = developmentUser;

        return (
          <div className="flex items-center gap-[8px] theme_btn_item">
            {/* Mark As Present button */}
            {row.original.employeeID == "-" && allowedPresentIDs && (
              <button
                className="dash-action-link cursor-pointer green-status-atted"
                onClick={() => {
                  setSelectedRowData(row.original);
                  setMarkPresentPopup(true);
                }}
              >
                <span className="text-sm dark:text-white">Mark As Present</span>
              </button>
            )}

            {/* Mark As Absent button */}
            {row.original.employeeID && allowedAbsentIDs && (
              <button
                className="dash-action-link cursor-pointer green-status-absent"
                onClick={() => {
                  setIsPopupOpen(true);
                  setSelectedClockInID(row.original.id);
                }}
              >
                <div className="text-sm dark:text-[#e53935]">
                  Delete Record
                </div>
              </button>
            )}

            {allowedAbsentIDs &&
              row.original.isOnLeave == false &&
              !row.original.isExtraDay && (
                <button
                  className="dash-action-link cursor-pointer green-status-absent btn_mark_leave"
                  onClick={() => {
                    const date = new Date(`${row.original.rawDate}T00:00:00Z`);
                    const formatted = [{ date: date, isHalfDay: false }];

                    setIsLeavePopupOpen(true);
                    setSelectedDate(formatted);
                    setSelectedClockInID(row.original.id);
                  }}
                >
                  <span className="text-sm dark:text-white">Mark Leave</span>
                </button>
              )}

            {row.original.employeeID &&
              allowedAbsentIDs &&
              !row.original.isExtraDay &&   !row.original.isNightShift && (
                <button
                  className="extraday-status_items flex justify-center items-center"
                  onClick={() => {
                    setSelectedClockInID(row.original.id);
                    setSelectedExtraDay(row.original.rawDate);
                    setIsExtraDayPopupOpen(true);
                  }}
                >
                  <span className="text-sm dark:text-[#fff]">
                    Add Extra Day
                  </span>
                </button>
              )}
            {row.original.employeeID &&
              allowedAbsentIDs &&
              !row.original.isNightShift && !row.original.isExtraDay && (
                <button
                  className="extraday-status_items flex justify-center items-center"
                  onClick={() => {
                    setSelectedClockInID(row.original.id);
                    setSelectedNightShift(row.original.rawDate);
                    setIsNightShiftPopupOpen(true);
                  }}
                >
                  <span className="text-sm dark:text-[#fff]">
                    Convert Night Shift
                  </span>
                </button>
              )}
          </div>
        );
      },
    },
  ];

  async function handleAddNightShift() {
    if (!selectedClockInID) {
      showError("Please Try Again.");
      return;
    }

    try {
      const response = await setNightShift({
        variables: {
          clock_in_id: parseInt(selectedClockInID),
        },
      });

      const result = response.data?.set_night_shift;

      if (result?.status_code === 200) {
        showSuccess(result.message);

        setIsNightShiftPopupOpen(false);
        setSelectedNightShift("");
        setSelectedClockInID("");

        refetchAttendanceData();
      } else {
        showError(result?.message || "Unable to add night shift.");
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        showError(error.message);
      } else {
        showError("Something went wrong.");
      }
    }
  }

  async function handleAddExtraDay() {
    if (!selectedClockInID) {
      showError("Please Try Again.");
      return;
    }

    try {
      const response = await setExtraDay({
        variables: {
          clock_in_id: parseInt(selectedClockInID),
        },
      });

      const result = response.data?.set_extra_day;

      if (result?.status_code === 200) {
        showSuccess(result.message);

        setIsExtraDayPopupOpen(false);
        setSelectedExtraDay("");
        setSelectedClockInID("");

        refetchAttendanceData();
      } else {
        showError(result?.message || "Unable to add extra day.");
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        showError(error.message);
      } else {
        showError("Something went wrong.");
      }
    }
  }

  const formattedData = useMemo(() => {
    if (
      clockInsData &&
      clockInsData.clock_ins_by_employee_id.status_code === 200
    ) {
      const { clock_ins = [], missed_clock_ins = [] } =
        clockInsData.clock_ins_by_employee_id;

      const clockInsMap = clock_ins.reduce((acc: any, c: any) => {
        const date = c.check_in_time.split("T")[0];
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(c);
        return acc;
      }, {});

      const missedMap = missed_clock_ins.reduce((acc: any, m: any) => {
        acc[m.missed_date] = m;
        return acc;
      }, {});

      const allDatesSet = new Set<string>();
      clock_ins.forEach((c: any) =>
        allDatesSet.add(c.check_in_time.split("T")[0]),
      );
      missed_clock_ins.forEach((m: any) => allDatesSet.add(m.missed_date));
      const allDates = Array.from(allDatesSet).sort();

      return allDates.flatMap((date) => {
        const clockIns = clockInsMap[date] || [];
        const missed = missedMap[date];
        const weekDay = clockIns[0]?.week_day || missed?.week_day || "";
        const onLeave = missed?.on_leave || false;

        // If there are clock-ins, return one row per clock-in
        if (clockIns.length > 0) {
          return clockIns.map((clockIn: any) => {
            const checkIn = clockIn?.check_in_time;
            const checkOut = clockIn?.check_out_time;

            return {
              id: clockIn?.id ? clockIn?.id : "",
              date: formatPrettyDateWithWeekday(date, weekDay),
              rawDate: date,
              employeeID: clockIn?.employees_id || "-",
              checkInTime: clockIn?.check_in_time
                ? formatDateTime(clockIn.check_in_time)
                : "-",
              checkOutTime: clockIn?.check_out_time
                ? formatDateTime(clockIn.check_out_time)
                : "-",
              isCheckIn: clockIn?.is_check_in ?? "-",
              agencyID: clockIn?.agencies_id ?? "-",
              checkedBy: clockIn?.checked_by ?? "-",
              duration:
                checkIn && checkOut ? formatDuration(checkIn, checkOut) : "-",
              isOnLeave: onLeave,
              isExtraDay: clockIn?.is_extra_day ?? false,
              isNightShift: clockIn?.is_night_shift ?? false,
            };
          });
        }

        // If only missed clock-in, return one row
        return [
          {
            date: formatPrettyDateWithWeekday(date, weekDay),
            rawDate: date,
            employeeID: "",
            checkInTime: "-",
            checkOutTime: "-",
            isCheckIn: "-",
            agencyID: "-",
            checkedBy: "-",
            isOnLeave: onLeave,
          },
        ];
      });
    } else {
      return [];
    }
  }, [clockInsData]);

  function handleSelectMonth(key: number, name: string): void {
    setValue("monthName", key);
  }

  function handleSelectYear(key: number, name: string): void {
    setValue("yearName", key);
  }

  const refetchAttendanceData = () => {
    const selectedMonth = parseInt(watchMonth);
    const selectedYear = parseInt(watchYear);

    if (watchAssignedMemberID && selectedMonth && selectedYear) {
      const firstDayOfMonth = startOfMonth(
        new Date(selectedYear, selectedMonth - 1),
      );
      const lastDayOfMonth = endOfMonth(
        new Date(selectedYear, selectedMonth - 1),
      );
      const today = new Date();

      const isCurrentMonth =
        selectedYear === today.getFullYear() &&
        selectedMonth === today.getMonth() + 1;

      const formattedStartDate = format(firstDayOfMonth, "yyyy-MM-dd");
      const formattedEndDate = format(
        isCurrentMonth ? today : lastDayOfMonth,
        "yyyy-MM-dd",
      );

      getClockInsByEmpID({
        variables: {
          employee_id: parseInt(watchAssignedMemberID),
          start_date: formattedStartDate,
          end_date: formattedEndDate,
        },
      });
    }
  };

  async function handleMarkAbsent() {
    if (selectedClockInID) {
      try {
        const response = await deleteClockInOut({
          variables: {
            clock_in_id: parseInt(selectedClockInID),
            is_add_leave: false,
          },
        });
        if (response.data.delete_clock_in_out.status_code === 200) {
          showSuccess(response.data.delete_clock_in_out.message);
          setIsPopupOpen(false);
          setSelectedClockInID("");
          refetchAttendanceData();
        }
        if (response.data.delete_clock_in_out.status_code === 400) {
          showError(response.data.delete_clock_in_out.message);
        }
      } catch (error) {
        if (error instanceof ApolloError) {
          showError(error.message);
        }
      }
    } else {
      showError("Please Try Again.");
    }
  }

  async function handleMarkLeave(leaveObj: any) {
    if (selectedClockInID) {
      try {
        const response = await deleteClockInOut({
          variables: {
            clock_in_id: parseInt(selectedClockInID),
            is_add_leave: true,
            leave_apply_input: leaveObj,
          },
        });
        if (response.data.delete_clock_in_out.status_code === 200) {
          showSuccess(response.data.delete_clock_in_out.message);
          setIsLeavePopupOpen(false);
          setSelectedClockInID("");
          setSelectedDate([]);

          refetchAttendanceData();
        }
        if (response.data.delete_clock_in_out.status_code === 400) {
          showError(response.data.delete_clock_in_out.message);
        }
      } catch (error) {
        if (error instanceof ApolloError) {
          showError(error.message);
        }
      }
    } else {
      try {
        const response = await deleteClockInOut({
          variables: {
            clock_in_id: null,
            is_add_leave: true,
            leave_apply_input: leaveObj,
          },
        });
        if (response.data.delete_clock_in_out.status_code === 200) {
          showSuccess(response.data.delete_clock_in_out.message);
          setIsLeavePopupOpen(false);
          setSelectedClockInID("");
          setSelectedDate([]);

          refetchAttendanceData();
        }
        if (response.data.delete_clock_in_out.status_code === 400) {
          showError(response.data.delete_clock_in_out.message);
        }
      } catch (error) {
        if (error instanceof ApolloError) {
          showError(error.message);
        }
      }
    }
  }

  return (
    <>
      <div
        id="cts-respon-p-dv"
        className="w-full flex flex-col items-start justify-start py-40 px-20  mq575:px-5 box-border gap-[20px] max-w-[100%] mq1425:max-w-full"
      >
        <div className="min-w-full flex flex-col items-start justify-start gap-6">
          <BreadCumb />

          <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
            <img
              className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
              alt=""
              src={backArrow}
              onClick={() => navigate(-1)}
            />
            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
              Attendance
            </h3>
          </div>
          <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
              {isAdmin && (
                <SearchableInputsDropdown
                  label="Employee*"
                  initialValue={watchAssignedMember}
                  placeHolder="Select Member"
                  options={assignedMemberOptions}
                  onSelect={(key: number, name: string) => {
                    // setClearMemberSelected(false);
                    setValue("assignedMemberID", key);
                    setValue("assignedMember", name);
                    // trigger('assignedMember');
                  }}
                  errorMessage={errors?.assignedMember}
                  // clearSelected={clearMemberSelected}
                />
              )}

              <SearchableInputsDropdown
                label="Month*"
                initialValue={
                  monthOptions.find((m) => m.key === watchMonth)?.name || ""
                }
                placeHolder="Select Month"
                options={monthOptions}
                onSelect={handleSelectMonth}
                errorMessage={errors?.monthName}
              />

              <SearchableInputsDropdown
                label="Year*"
                initialValue={
                  yearOptions.find((y) => y.key === watchYear)?.name || ""
                }
                placeHolder="Select Year"
                options={yearOptions}
                onSelect={handleSelectYear}
                errorMessage={errors?.yearName}
              />
            </div>

            {formattedData?.length > 0 ? (
              <div className="w-full viewattendance_dv">
                <DynamicTable
                  filteredData={formattedData}
                  selectedColumns={columns}
                  sorting={sorting}
                  setSorting={setSorting}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  setMainTable={setMainTable}
                  setMainTableUpdate={setMainTableUpdate}
                />
              </div>
            ) : (
              <div className="self-stretch rounded-lg  overflow-hidden rounded-lg flex flex-col items-center justify-center py-[20px] px-[20px] gap-[8px] text-center text-text-text-secondary mq450:pl-5 mq450:pr-5 mq450:box-border">
                <div className="self-stretch bg-white rounded-lg bg-slate-100 overflow-hidden dark:bg-slate-700 flex flex-col items-center justify-center py-[110px] px-[53px] gap-[8px] text-center text-text-text-secondary mq450:pl-5 mq450:pr-5 mq450:box-border">
                  <p className="relative my-0">No data available</p>
                </div>
              </div>
            )}
          </div>
          {(clockInsDataLoading ||
            employeesDataLoading ||
            deleteClockInOutLoading ||
            setExtraDayLoading ||
            setNightShiftLoading) && <Loader />}
        </div>
      </div>

      {markPresentPopup && selectedRowData && (
        <MarkPresentPopup
          isOpen={markPresentPopup}
          onClose={() => {
            setMarkPresentPopup(false);
            setSelectedRowData(null);
          }}
          selectedDate={selectedRowData.rawDate}
          employeeId={watchAssignedMemberID}
          refetch={refetchAttendanceData}
        />
      )}

      {isPopupOpen && (
        <NotifyPopup
          onsubmit={handleMarkAbsent}
          back={() => {
            setIsPopupOpen(false);
          }}
          isDelete={false}
          showMessage={"Delete Record."}
        />
      )}

      {isNightShiftPopupOpen && (
        <NotifyPopup
          onsubmit={handleAddNightShift}
          back={() => {
            setIsNightShiftPopupOpen(false);
            setSelectedNightShift("");
            setSelectedClockInID("");
          }}
          isDelete={false}
          notDelete={true}
          showMessage={`Do you want to add night shift on ${format(
            new Date(`${selectedNightShift}T00:00:00`),
            "MM-dd-yyyy",
          )}?`}
        />
      )}

      {isExtraDayPopupOpen && (
        <NotifyPopup
          onsubmit={handleAddExtraDay}
          back={() => {
            setIsExtraDayPopupOpen(false);
            setSelectedExtraDay("");
            setSelectedClockInID("");
          }}
          isDelete={false}
          notDelete={true}
          showMessage={`Do you want to add extra day on ${format(selectedExtraDay, "MM-dd-yyyy")}?`}
        />
      )}

      {isLeavePopupOpen && (
        <LeaveDetailsPopup
          selectedDates={selectedDate}
          emplID={watchAssignedMemberID}
          onClose={() => {
            setIsLeavePopupOpen(false);
            setSelectedDate([]);
            setSelectedClockInID("");
          }}
          onSubmitAndClose={handleMarkLeave}
        />
      )}
    </>
  );
};

export default ViewAttendance;
